import styled, { css } from "styled-components/macro";
import { mediaQueries } from "../styles/media";
import { flow } from "../styles/flow";

// Two prose scales. Shared values (leading, h3/h4 sizes) live on the base element below;
// each variant sets only what differs. `regular` is long-form (terms); `compact` is the
// smaller dashboard-message scale, with headings capped at the h3 size.
const variants = {
  regular: css`
    --prose-measure: 34em;
    --prose-flow: 1.25em;
    --prose-table-flow: 2em;
    --prose-h1-size: 1.5em;
    --prose-h2-size: 1.25em;

    font-size: var(--juno-font-size-body-sm);
    ${mediaQueries.md} {
      font-size: var(--juno-font-size-body-md);
    }
  `,
  compact: css`
    --prose-flow: 1em;
    --prose-table-flow: 1em;
    --prose-h1-size: 1.1em;
    --prose-h2-size: 1.1em;

    font-size: 14px;
    ${mediaQueries.md} {
      font-size: 16px;
    }
  `,
};

// Formats a block of server-rendered HTML (markdown converted upstream by the staff app)
// with typographic defaults, like Tailwind's `prose`. Prose does not sanitise: it trusts
// the source. Typography only, no padding: the $variant
// picks the scale, the container places it. white-space: normal so source newlines between
// tags don't render as breaks. Vertical rhythm comes from flow(); --flow-space tunes
// individual gaps. --prose-measure caps line length (none unless a variant sets it).
const Prose = styled.div`
  --prose-measure: none;

  /* Shared by both variants. */
  --prose-leading: 1.5;
  --prose-h3-size: 1.1em;
  --prose-h4-size: 1em;

  ${(props) => variants[props.$variant] || variants.regular}

  white-space: normal;
  line-height: var(--prose-leading);
  max-width: var(--prose-measure);

  ${flow("var(--prose-flow)")}

  /* Heading line-heights tighten as size grows; --flow-space is the space above. */
  h1 {
    font-size: var(--prose-h1-size);
    line-height: 1.2;
    --flow-space: 2em;
  }

  h2 {
    font-size: var(--prose-h2-size);
    line-height: 1.25;
    --flow-space: 2em;
  }

  h3 {
    font-size: var(--prose-h3-size);
    line-height: 1.3;
    --flow-space: 1.6em;
  }

  h4 {
    font-size: var(--prose-h4-size);
    line-height: 1.4;
    --flow-space: 1.5em;
  }

  /* Gap below a heading is its own margin-bottom, so it scales with the heading, not the
     body. The follower's flow gap is dropped below so the two don't stack. */
  h1,
  h2,
  h3,
  h4 {
    margin-bottom: 0.6em;
    font-weight: var(--juno-font-weight-bold);
  }

  /* Pin body text to the inherited base, overriding the global p font-size/line-height. */
  p,
  li,
  th,
  td {
    font-size: inherit;
    line-height: inherit;
  }

  em {
    font-style: italic;
  }

  del {
    text-decoration: line-through;
  }

  ins {
    text-decoration: underline;
  }

  hr {
    --flow-space: 2.5em;
    border: 0;
    border-top: 1px solid var(--juno-color-pale-grey);
  }

  hr + * {
    --flow-space: 2.5em;
  }

  ul,
  ol {
    padding-left: 1.5em;
  }

  li {
    margin-bottom: 0.5em;
  }

  li:last-child {
    margin-bottom: 0;
  }

  /* Nested lists hug their parent item rather than carrying a full block gap. */
  li > ul,
  li > ol {
    margin-top: 0.5em;
  }

  /* Task lists: the checkbox replaces the bullet. */
  li:has(> input[type="checkbox"]) {
    list-style: none;
    margin-left: -1.5em;
  }

  li > input[type="checkbox"] {
    margin-right: 0.5em;
  }

  /* Long links should wrap rather than overflow. Colours come from GlobalStyle. */
  a {
    overflow-wrap: anywhere;
  }

  /* Match JunoTable (components/Table): borderless, just a row rule, sized to content
     (full width reads oddly for small figure tables). Leaving width unset also lets a
     JunoTable inside Prose keep its own width: 100%. */
  table {
    --flow-space: var(--prose-table-flow);
    border-collapse: collapse;
    max-width: 100%;
  }

  tr {
    border-bottom: 1px solid var(--juno-color-pale-grey);
  }

  /* Pointer-only so the row hover doesn't flash on touch. */
  ${mediaQueries.md} {
    tbody tr:hover {
      background-color: rgba(0, 0, 0, 0.04);
    }
  }

  /* Unlike JunoTable, these tables don't stack on small screens, so cells keep horizontal
     padding at every width or the columns collide. */
  th,
  td {
    text-align: left;
    padding: 0.25em 0.5em;
    ${mediaQueries.md} {
      padding: 0.5em 1em;
    }
  }

  code {
    font-family: var(--juno-font-family-mono);
    font-size: 0.9em;
    background-color: var(--juno-color-off-white);
    border: 1px solid var(--juno-color-pale-grey);
    border-radius: var(--juno-radius-sm);
    padding: 0.1em 0.3em;
  }

  pre {
    --flow-space: 1.5em;
    background-color: var(--juno-color-off-white);
    border: 1px solid var(--juno-color-pale-grey);
    border-radius: var(--juno-radius-md);
    padding: 12px;
    overflow-x: auto;
  }

  /* Inside a pre the block already supplies the frame, so reset the inline chrome. */
  pre code {
    background-color: transparent;
    border: 0;
    border-radius: 0;
    padding: 0;
  }

  blockquote {
    --flow-space: 1.6em;
    margin-left: 0;
    padding-left: 1em;
    border-left: 3px solid var(--juno-color-pale-grey);
    color: var(--juno-color-grey);
  }

  /* Element after a heading takes its gap from the heading's margin-bottom, so drop its
     own. The compound selector outweighs a bare element's own --flow-space on specificity.
     Tables keep their gap. */
  h1 + *:not(table),
  h2 + *:not(table),
  h3 + *:not(table),
  h4 + *:not(table) {
    --flow-space: 0;
  }
`;

export default Prose;
